import { useEffect, useRef, useState } from 'react';
import { addHours, addMinutes, addSeconds, format } from 'date-fns';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Program, Track } from '../data/mockData';
import { AlertCircle, Calendar, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { parseApiDate, toLocalInputValue } from '../utils/dateTime';
import { AvailabilityCalendarModal } from './AvailabilityCalendarModal';

interface ScheduleProgramDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  onSave: (program: Omit<Program, 'id' | 'channelId'>) => Promise<void>;
  existingProgram: Omit<Program, 'id' | 'channelId'> | null;
  tracks?: Track[];
  programs?: Program[];
}

export function ScheduleProgramDrawer({
  isOpen,
  onClose,
  onBack,
  onSave,
  existingProgram,
  tracks,
  programs,
}: ScheduleProgramDrawerProps) {
    const getApiErrorMessage = (error: unknown, fallback: string) => {
      if (error instanceof Error && error.message.trim().length > 0) {
        return error.message;
      }

      if (error && typeof error === 'object') {
        const maybeError = error as { data?: unknown; status?: number | string };
        if (maybeError.data && typeof maybeError.data === 'object') {
          const maybeData = maybeError.data as { detail?: unknown; message?: unknown };
          if (typeof maybeData.detail === 'string') {
            return maybeData.detail;
          }
          if (typeof maybeData.message === 'string') {
            return maybeData.message;
          }
        }

        if (typeof maybeError.data === 'string' && maybeError.data.trim().length > 0) {
          return maybeError.data;
        }
      }

      return fallback;
    };

  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAvailability, setShowAvailability] = useState(false);
  const [scheduleType, setScheduleType] = useState<'one-time' | 'recurring'>('one-time');
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>('weekly');
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [endCondition, setEndCondition] = useState<'endDate'>('endDate');
  const [recEndDate, setRecEndDate] = useState('');
  const startDateTimeRef = useRef<HTMLInputElement>(null);
  const endDateTimeRef = useRef<HTMLInputElement>(null);

  const getDurationSeconds = (trackId: string | number | null | undefined) => {
    if (!trackId || !tracks) {
      return null;
    }

    const track = tracks.find((item) => String(item.id) === String(trackId));
    if (!track || !track.duration) {
      return null;
    }

    const parts = track.duration.split(':').map((value) => Number(value));
    if (parts.some((value) => Number.isNaN(value))) {
      return null;
    }

    let totalSeconds = 0;
    if (parts.length === 3) {
      totalSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      totalSeconds = parts[0] * 60 + parts[1];
    } else if (parts.length === 1) {
      totalSeconds = parts[0] * 60;
    }

    if (totalSeconds <= 0) {
      return null;
    }

    return totalSeconds;
  };

  const resetForm = () => {
    setStartDateTime('');
    setEndDateTime('');
    setErrors({});
    setScheduleType('one-time');
    setFrequency('weekly');
    setSelectedDays([]);
    setEndCondition('endDate');
    setRecEndDate('');
  };

  const updateEndFromDuration = (startValue: string) => {
    if (!existingProgram) {
      return;
    }

    const seconds = getDurationSeconds(existingProgram.trackId);
    if (!seconds) {
      return;
    }

    const start = new Date(startValue);
    if (Number.isNaN(start.getTime())) {
      return;
    }

    setEndDateTime(toLocalInputValue(addSeconds(start, seconds)));
  };

  useEffect(() => {
    if (existingProgram) {
      const startValue = existingProgram.startDateTime
        ? toLocalInputValue(parseApiDate(existingProgram.startDateTime))
        : '';
      setStartDateTime(startValue);

      const durationSecs = getDurationSeconds(existingProgram.trackId);
      if (existingProgram.endDateTime) {
        const endValue = toLocalInputValue(parseApiDate(existingProgram.endDateTime));
        if (startValue && durationSecs) {
          const start = new Date(startValue);
          const defaultEnd = addHours(start, 1);
          const existingEnd = new Date(endValue);
          const diff = Math.abs(existingEnd.getTime() - defaultEnd.getTime());
          if (diff <= 60000) {
            setEndDateTime(toLocalInputValue(addSeconds(start, durationSecs)));
            return;
          }
        }
        setEndDateTime(endValue);
      } else if (startValue) {
        if (durationSecs) {
          const start = new Date(startValue);
          setEndDateTime(toLocalInputValue(addSeconds(start, durationSecs)));
        } else {
          updateEndFromDuration(startValue);
        }
      } else {
        setEndDateTime('');
      }

      // Populate recurrence fields
      if (existingProgram.scheduleType === 'recurring' && existingProgram.recurrence) {
        setScheduleType('recurring');
        setFrequency(existingProgram.recurrence.frequency === 'daily' ? 'daily' : 'weekly');
        setSelectedDays(existingProgram.recurrence.daysOfWeek || []);
        setEndCondition('endDate');
        if (existingProgram.recurrence.endDate) {
          setRecEndDate(format(new Date(existingProgram.recurrence.endDate), 'yyyy-MM-dd'));
        }
      } else {
        setScheduleType('one-time');
        setFrequency('weekly');
        setSelectedDays([]);
        setEndCondition('endDate');
        setRecEndDate('');
      }
    } else {
      resetForm();
    }
  }, [existingProgram, tracks]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!startDateTime) {
      newErrors.startDateTime = 'Start date and time is required';
    } else if (new Date(startDateTime) < new Date()) {
      newErrors.startDateTime = 'Start date and time cannot be in the past';
    }

    if (!endDateTime) {
      newErrors.endDateTime = 'End date and time is required';
    }

    if (scheduleType === 'recurring') {
      if (frequency === 'weekly' && selectedDays.length === 0) {
        newErrors.recurring = 'Select at least one day of the week';
      }
      if (endCondition === 'endDate' && !recEndDate) {
        newErrors.recEndDate = 'Please specify an end date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !existingProgram) {
      return;
    }

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    const programData: Omit<Program, 'id' | 'channelId'> = {
      title: existingProgram.title,
      trackId: existingProgram.trackId,
      startDateTime: start,
      endDateTime: end,
      scheduleType,
      description: existingProgram.description,
      thumbnail: existingProgram.thumbnail,
    };

    if (scheduleType === 'recurring') {
      programData.recurrence = {
        frequency,
        daysOfWeek: frequency === 'weekly' ? selectedDays : undefined,
        endCondition,
        endDate: endCondition === 'endDate' && recEndDate ? new Date(recEndDate) : undefined,
      };
    }

    try {
      await onSave(programData);
      toast.success('Program scheduled successfully');
      handleClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to schedule program.'));
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleBackToProgram = () => {
    resetForm();
    if (onBack) {
      onBack();
      return;
    }
    onClose();
  };

  const isValid = startDateTime && endDateTime && Object.keys(errors).length === 0;
  const hasDuration = existingProgram ? getDurationSeconds(existingProgram.trackId) !== null : false;

  const handleStartDateTimeChange = (value: string) => {
    setStartDateTime(value);
    if (!value) {
      setErrors((prev) => { const { startDateTime: _, ...rest } = prev; return rest; });
      return;
    }

    if (new Date(value) < new Date()) {
      setErrors((prev) => ({ ...prev, startDateTime: 'Start date and time cannot be in the past' }));
      return;
    }
    setErrors((prev) => { const { startDateTime: _, ...rest } = prev; return rest; });

    const durationSecs = existingProgram ? getDurationSeconds(existingProgram.trackId) : null;
    if (durationSecs) {
      const start = new Date(value);
      setEndDateTime(toLocalInputValue(addSeconds(start, durationSecs)));
      return;
    }

    const start = new Date(value);
    const currentEnd = endDateTime ? new Date(endDateTime) : null;
    if (!currentEnd || currentEnd <= start) {
      setEndDateTime(toLocalInputValue(addHours(start, 1)));
    }
  };

  const openDateTimePicker = (inputRef: React.RefObject<HTMLInputElement>) => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
      return;
    }

    input.focus();
  };

  return (
    <>
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-gray-900 border-gray-800 text-white overflow-y-auto p-0"
      >
        <SheetHeader className="px-6 sm:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6 border-b border-gray-800">
          <SheetTitle className="text-2xl font-bold text-white">Schedule Program</SheetTitle>
          <SheetDescription className="text-gray-400 mt-2">
            Set the schedule for this program
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 sm:px-8 py-6 sm:py-8 space-y-8">
          {/* Start Date & Time */}
          <div className="space-y-3 max-w-md">
            <Label htmlFor="datetime" className="text-sm font-semibold text-white">
              Start Date & Time (Local) <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <input
                ref={startDateTimeRef}
                id="datetime"
                type="datetime-local"
                value={startDateTime}
                min={toLocalInputValue(new Date())}
                onChange={(e) => handleStartDateTimeChange(e.target.value)}
                className="schedule-datetime-input bg-gray-800 border-gray-700 text-white h-11 w-full rounded-md border px-3 pr-11"
              />
              <button
                type="button"
                onClick={() => openDateTimePicker(startDateTimeRef)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 hover:text-blue-200"
                aria-label="Open start date and time picker"
              >
                <Calendar className="size-4" />
              </button>
            </div>
            {errors.startDateTime && (
              <p className="text-xs text-red-400 mt-1">{errors.startDateTime}</p>
            )}
          </div>

          {/* End Date & Time */}
          <div className="space-y-3 max-w-md">
            <Label htmlFor="enddatetime" className="text-sm font-semibold text-white">
              End Date & Time (Local) <span className="text-red-500">*</span>
            </Label>
            {hasDuration && (
              <p className="text-xs text-blue-400">Auto-calculated from audio duration</p>
            )}
            <div className="relative">
              <input
                ref={endDateTimeRef}
                id="enddatetime"
                type="datetime-local"
                value={endDateTime}
                onChange={(e) => { if (!hasDuration) setEndDateTime(e.target.value); }}
                readOnly={hasDuration}
                className={`schedule-datetime-input bg-gray-800 border-gray-700 text-white h-11 w-full rounded-md border px-3 pr-11 ${hasDuration ? 'opacity-60 cursor-not-allowed' : ''}`}
              />
              {!hasDuration && (
              <button
                type="button"
                onClick={() => openDateTimePicker(endDateTimeRef)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300 hover:text-blue-200"
                aria-label="Open end date and time picker"
              >
                <Calendar className="size-4" />
              </button>
              )}
            </div>
            {errors.endDateTime && (
              <p className="text-xs text-red-400 mt-1">{errors.endDateTime}</p>
            )}
          </div>

          {/* Check Availability */}
          {programs && programs.length > 0 && (
            <div className="max-w-md">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAvailability(true)}
                className="gap-2 border-gray-700 text-black hover:text-white hover:bg-gray-800"
              >
                <Eye className="size-4" />
                Check Availability
              </Button>
            </div>
          )}

          {/* Schedule Type Toggle */}
          <div className="space-y-3 max-w-md">
            <Label className="text-sm font-semibold text-white">Schedule Type</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setScheduleType('one-time')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  scheduleType === 'one-time'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'
                }`}
              >
                One-time
              </button>
              <button
                type="button"
                onClick={() => setScheduleType('recurring')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  scheduleType === 'recurring'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Recurring
              </button>
            </div>
          </div>

          {/* Recurring Options */}
          {scheduleType === 'recurring' && (
            <div className="space-y-5 p-5 bg-gray-800/60 rounded-xl border border-gray-700 max-w-md">
              {/* Frequency */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-300">Frequency</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFrequency('daily')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      frequency === 'daily'
                        ? 'bg-blue-600/80 text-white'
                        : 'bg-gray-800 border border-gray-600 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    Daily
                  </button>
                  <button
                    type="button"
                    onClick={() => setFrequency('weekly')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      frequency === 'weekly'
                        ? 'bg-blue-600/80 text-white'
                        : 'bg-gray-800 border border-gray-600 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    Weekly
                  </button>
                </div>
              </div>

              {/* Days of Week (for weekly) */}
              {frequency === 'weekly' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-300">Repeat on</Label>
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { value: 0, label: 'S' },
                      { value: 1, label: 'M' },
                      { value: 2, label: 'T' },
                      { value: 3, label: 'W' },
                      { value: 4, label: 'T' },
                      { value: 5, label: 'F' },
                      { value: 6, label: 'S' },
                    ].map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() =>
                          setSelectedDays((prev) =>
                            prev.includes(day.value)
                              ? prev.filter((d) => d !== day.value)
                              : [...prev, day.value]
                          )
                        }
                        className={`w-10 h-10 rounded-full text-sm font-semibold transition-all ${
                          selectedDays.includes(day.value)
                            ? 'bg-blue-600 text-white ring-2 ring-blue-400/50'
                            : 'bg-gray-800 border border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white'
                        }`}
                        title={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day.value]}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  {errors.recurring && (
                    <p className="text-xs text-red-400 mt-1">{errors.recurring}</p>
                  )}
                </div>
              )}

              {/* End Condition */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-300">Ends</Label>
                <div className="space-y-2">
                  {/* Till date */}
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-700 bg-gray-800 hover:bg-gray-700/60 transition-colors cursor-pointer">
                    <input
                      type="radio"
                      name="endCondition"
                      checked={endCondition === 'endDate'}
                      onChange={() => setEndCondition('endDate')}
                      className="accent-blue-600"
                    />
                    <span className="text-sm text-gray-300">Till date</span>
                  </label>
                  {endCondition === 'endDate' && (
                    <div className="pl-9">
                      <Input
                        type="date"
                        value={recEndDate}
                        onChange={(e) => setRecEndDate(e.target.value)}
                        className="bg-gray-800 border-gray-700 text-white h-10 max-w-[200px]"
                      />
                      {errors.recEndDate && (
                        <p className="text-xs text-red-400 mt-1">{errors.recEndDate}</p>
                      )}
                    </div>
                  )}


                </div>
              </div>
            </div>
          )}

          {/* Validation Banner */}
          {Object.keys(errors).length > 0 && (
            <div className="p-4 bg-red-900/30 border border-red-700/50 rounded-lg flex gap-3">
              <AlertCircle className="size-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-200">Program config invalid</p>
                <p className="text-xs text-red-300 mt-1">
                  Please fix the errors above to save the schedule.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-6 sm:px-8 py-4 sm:py-5">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleBackToProgram}
              className="flex-1 border-gray-700 text-black hover:bg-gray-800 hover:text-white"
            >
              Back to Program
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isValid}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Save Schedule
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>

    <AvailabilityCalendarModal
      isOpen={showAvailability}
      onClose={() => setShowAvailability(false)}
      programs={programs || []}
    />
    </>
  );
}
